import { motion } from '../lib/motion';
import { MapPin, Phone, Mail, Clock, CircleCheck } from 'lucide-react';

import { SITE_IMAGES } from '../data/images';
import type { HomepageContent } from '../lib/types';
import { anneesExperience } from '../lib/anciennete';
import DevisForm from './DevisForm';
import { ADRESSE_COURTE } from '../data/entreprise';
import { REGISTRE } from '../data/registre';


const FACEBOOK_URL = 'https://www.facebook.com/GrammeDemenagements';

// Même correction qu'en tête de WhyUs : « assurance tous risques incluse » ne
// pouvait pas cohabiter avec l'« assurance de base incluse » de llms.txt. Le
// libellé sort du registre, où le point reste ouvert auprès de Gramme.
const REASSURANCES = () => [
  'Réponse garantie sous 24h ouvrables',
  'Devis 100 % gratuit et sans engagement',
  REGISTRE['assurance.incluse'].court,
  `+${anneesExperience()} ans d'expérience à votre service`,
];

interface Props {
  data?: HomepageContent['contact'] | null;
  /**
   * `complet` (défaut) : rendu inchangé — accueil, /contact-devis,
   * /zones-intervention, satellites. Aucune de ces pages ne passe la prop, et
   * aucune ne bouge d'un pixel.
   *
   * `locale` : variante des pages communes. La colonne de gauche disparaît —
   * quatre puces de réassurance, coordonnées, horaires, photo d'équipe,
   * Facebook, numéro de TVA : environ 110 mots strictement identiques sur les
   * soixante-dix pages communes, et déjà présents dans le pied de page comme
   * dans le hero. Le formulaire, lui, ne change pas : mêmes champs, même
   * validation, même insertion.
   */
  variant?: 'complet' | 'locale';
  /**
   * Commune pré-remplie dans « Ville de départ », sur les pages locales.
   * Le champ reste modifiable : c'est une amorce, pas une valeur imposée.
   */
  villeParDefaut?: string;
  /**
   * Annotations WebMCP du formulaire. À passer à `false` sur les pages dont le
   * hero porte déjà le formulaire annoté — accueil, communes — pour qu'un
   * agent ne voie qu'un seul outil `demander_devis_demenagement` par page.
   */
  annoterPourAgents?: boolean;
}

export default function ContactForm({
  data,
  variant = 'complet',
  villeParDefaut,
  annoterPourAgents = true,
}: Props) {
  const locale = variant === 'locale';

  return (
    <section id="contact" className="bg-offwhite py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Variante locale : un titre, une ligne, le formulaire. Tout le reste
            — argumentaire, coordonnées, photo — est du texte identique d'une
            commune à l'autre, et déjà servi ailleurs sur la même page. */}
        {locale && (
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-black uppercase text-navy leading-tight">
              {villeParDefaut ? `Votre devis pour ${villeParDefaut}` : 'Votre devis gratuit'}
            </h2>
            <p className="text-muted text-lg mt-3">
              Réponse sous 24 heures ouvrables, sans engagement.
            </p>
          </div>
        )}

        <div
          className={
            locale
              ? 'max-w-2xl mx-auto'
              : 'grid grid-cols-1 lg:grid-cols-[40%_60%] gap-10 lg:gap-16 items-start'
          }
        >

          {/* LEFT — Vendre le devis */}
          {!locale && (
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-8"
          >
            <div>
              <span className="inline-block bg-yellow text-navy text-[11px] uppercase tracking-[0.2em] font-bold rounded-full px-4 py-1.5 mb-4">
                DEVIS GRATUIT EN 24H
              </span>
              <h2 className="text-3xl md:text-4xl font-black uppercase text-navy leading-tight">
                {data?.title || 'Demandez votre devis personnalisé'}
              </h2>
              <p className="text-muted text-lg mt-3 leading-relaxed">
                {data?.subtitle || 'Réponse garantie sous 24h. Gratuit et sans engagement.'}
              </p>
            </div>

            <ul className="space-y-3">
              {REASSURANCES().map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-yellow/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CircleCheck className="w-4 h-4 text-navy" />
                  </div>
                  <span className="text-navy/80 text-[15px] leading-snug">{item}</span>
                </li>
              ))}
            </ul>

            <div className="space-y-3">
              {[
                { icon: MapPin, text: ADRESSE_COURTE },
                { icon: Phone, text: '04 264 50 16', href: 'tel:+3242645016' },
                { icon: Mail, text: 'contact@demenagements-gramme.be', href: 'mailto:contact@demenagements-gramme.be' },
                { icon: Clock, text: 'Lun : 8h–18h | Mar–Ven : 8h–17h' },
              ].map(({ icon: Icon, text, href }) => (
                <div key={text} className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-yellow flex-shrink-0" />
                  {href ? (
                    <a href={href} className="inline-block py-1.5 text-navy/80 hover:text-navy transition-colors text-sm">
                      {text}
                    </a>
                  ) : (
                    <span className="text-navy/70 text-sm">{text}</span>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden aspect-[16/9] shadow-md">
              <img
                src={SITE_IMAGES.team.src}
                srcSet={SITE_IMAGES.team.srcSet}
                sizes="(min-width: 1024px) 600px, 100vw"
                alt={SITE_IMAGES.team.alt}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="flex items-center justify-between">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-10 h-10 rounded-full bg-navy items-center justify-center hover:bg-yellow transition-colors group"
                aria-label="Facebook Déménagements Gramme"
              >
                <svg className="w-5 h-5 text-yellow group-hover:text-navy transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              {/* `text-navy/30` valait 1,88:1 sur blanc — très en dessous du seuil.
                  `text-muted` est la teinte pleine prévue pour ce rôle, calibrée
                  à 5,55:1, et c'est bien une mention légale discrète. */}
              <p className="text-muted text-xs">TVA BE 0775.264.382</p>
            </div>
          </motion.div>
          )}

          {/* RIGHT — Formulaire, source unique : DevisForm. Le lien vers
              l'estimateur de volume par photos qui figurait ici n'existe plus ;
              la route /estimation-volume reste accessible par le menu. */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <DevisForm
              source="formulaire-contact"
              prefixeId="cf"
              ancre="devis"
              villeParDefaut={villeParDefaut}
              annoterPourAgents={annoterPourAgents}
            />
          </motion.div>

        </div>
      </div>

    </section>
  );
}
